import React from 'react';
import { BaasicDropdown, BaasicInput, InfoPanel, Loader } from 'core/components';
import { defaultTemplate } from 'core/hoc';
import { channelType } from 'common/constants';

/** Why a ShipStation warehouse list could not be offered, in words the user can act on. */
const listErrorReasonKey = {
	NO_CREDENTIALS: 'WAREHOUSECODES.SHIPSTATIONREASONNOCREDENTIALS',
	NO_V2_KEY: 'WAREHOUSECODES.SHIPSTATIONREASONNOV2KEY',
	AUTH_FAILED: 'WAREHOUSECODES.SHIPSTATIONREASONAUTHFAILED',
	UNAVAILABLE: 'WAREHOUSECODES.SHIPSTATIONREASONUNAVAILABLE',
};

const WarehouseCodesTemplate = ({
	t,
	viewStore: {
		loading,
		form,
		channel,
		modalParams,
		shouldRender,
		getWarehouseStatus,
		shipStationShipFromError,
		shipStationInventoryError,
		shipStationInvalidCount,
	},
}) => {
	if (loading) return <Loader />;

	const shipStationMessages = () => {
		const messages = [t('WAREHOUSECODES.SHIPSTATIONHINT')];
		const addListError = (columnKey, error) => {
			if (!error) return;
			messages.push(
				t('WAREHOUSECODES.SHIPSTATIONLISTUNAVAILABLE', {
					column: t(columnKey),
					reason: t(listErrorReasonKey[error] || listErrorReasonKey.UNAVAILABLE),
				})
			);
		};
		addListError('WAREHOUSECODES.SHIPSTATIONSHIPFROM', shipStationShipFromError);
		addListError('WAREHOUSECODES.SHIPSTATIONINVENTORYWAREHOUSE', shipStationInventoryError);
		if (shipStationInvalidCount > 0) {
			messages.push(t('WAREHOUSECODES.SHIPSTATIONINVALIDMAPPINGS', { count: shipStationInvalidCount }));
		}
		return messages;
	};

	return (
		<section className="layout__main__body">
			<form className="ReactModal__form" onSubmit={form.onSubmit}>
				<div className="ReactModal__header">
					<h3 className="spc--bottom--sml">{t('WAREHOUSECODES.TITLE')}</h3>
				</div>
				<div className="ReactModal__body">
					<div className="row">
						<div className="col col-sml-12 col-med-6 spc--bottom--xsml">
							<div className="card card--xsml card--wh-codes">
								<label className="display--b type--xsml type--wgt--bold type--uppercase type--color--grey-3">
									{t('WAREHOUSECODES.SELLER')}
								</label>
								<p className="type--lrg type--wgt--bold">
									{channel || modalParams.data.isEdit ? channel.seller.applicationTitle : '-'}
								</p>
							</div>
						</div>
						<div className="col col-sml-12 col-med-6 spc--bottom--xsml">
							<div className="card card--xsml card--wh-codes">
								<label className="display--b type--xsml type--wgt--bold type--uppercase type--color--grey-3">
									{t('WAREHOUSECODES.CHANNEL')}
								</label>
								<p className="type--lrg type--wgt--bold">{channel || modalParams.data.isEdit ? channel.name : '-'}</p>
							</div>
						</div>
					</div>
					{/* ShipStation needs BOTH columns and they hold different concepts, so it gets its own explanation
						in the standard info panel. Walmart uses ship node identifiers instead of warehouse codes,
						but they are equivalent, so it keeps the plain hint. */}
					{shouldRender(channelType.shipStation) ? (
						<div className="spc--bottom--med">
							<InfoPanel messages={shipStationMessages()} />
						</div>
					) : (
						<div className="message message--tny message--primary spc--bottom--med">
							{shouldRender(channelType.walmart) ? t('WAREHOUSECODES.WALMARTHINT') : t('WAREHOUSECODES.HINT')}
						</div>
					)}
					<table className="table--secondary table--secondary--warehouse table--secondary--codes spc--bottom--sml">
						<thead>
							{/* th value needs to be hardcoded. */}
							<tr>
								<th className="border">Warehouse</th>
								<th className="border">
									{shouldRender(channelType.walmart)
										? t('WAREHOUSECODES.SHIPNODEIDENTIFIER')
										: shouldRender(channelType.shipStation)
										? t('WAREHOUSECODES.SHIPSTATIONSHIPFROM')
										: t('WAREHOUSECODES.CODE')}
								</th>
								{shouldRender(channelType.amazon) && <th>{t('WAREHOUSECODES.MLICODE')}</th>}
								{(shouldRender(channelType.amazon) ||
									shouldRender(channelType.goFlow) ||
									shouldRender(channelType.sellerCloud) ||
									shouldRender(channelType.shipStation)) && (
									<th>
										{shouldRender(channelType.shipStation)
											? t('WAREHOUSECODES.SHIPSTATIONINVENTORYWAREHOUSE')
											: t('WAREHOUSECODES.IDENTIFIERONCHANNEL')}
									</th>
								)}
							</tr>
						</thead>
						<tbody>
							{form.$('warehouseCodes').map(item => {
								return (
									<tr key={item.id}>
										<td>
											<label className="type--base type--wgt--bold type--color--text">
												{item.$('warehouseName').value}
											</label>
										</td>
										<td>
											{/* ShipStation matches this against live warehouse NAMES, so it is picked from the
												account rather than typed — and the pick is only offered when we could read them. */}
											{shouldRender(channelType.shipStation) ? (
												<MappingCell
													t={t}
													dropdownStore={item.shipFromDropdownStore}
													value={item.$('name').value}
													status={getWarehouseStatus(item, true)}
												/>
											) : (
												<BaasicInput noSpc field={item.$('name')} />
											)}
										</td>
										{shouldRender(channelType.amazon) && (
											<td>
												<BaasicInput noSpc field={item.$('altName')} />
											</td>
										)}
										{(shouldRender(channelType.amazon) ||
											shouldRender(channelType.goFlow) ||
											shouldRender(channelType.sellerCloud) ||
											shouldRender(channelType.shipStation)) && (
											<td>
												{/* Editable for ShipStation only — the other channel types are auto-populated
													by ChannelService.SyncIdentifierOnChannel on save, so a typed value would be lost. */}
												{shouldRender(channelType.shipStation) ? (
													<MappingCell
														t={t}
														dropdownStore={item.inventoryDropdownStore}
														value={item.$('identifierOnChannel').value}
														status={getWarehouseStatus(item, false)}
													/>
												) : (
													<label className="type--base type--color--text">{item.$('identifierOnChannel').value}</label>
												)}
											</td>
										)}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
				<div className="ReactModal__footer">
					<button className="btn btn--med btn--primary spc--right--tny spc--bottom--sml" type="submit">
						{t('WAREHOUSECODES.SAVECODES')}
					</button>
					<button className="btn btn--med btn--outline spc--bottom--sml" onClick={() => modalParams.close()}>
						{t('CANCEL')}
					</button>
				</div>
			</form>
		</section>
	);
};

/**
 * One mappable ShipStation column: the picker plus a verdict on whether the saved mapping still resolves.
 * Without a dropdown store the list could not be read, so the value is shown read-only rather than as free text —
 * there would be nothing to check the typing against, which is the problem this replaced.
 * The icon sits inside the cell because the table is table-layout: fixed.
 */
const MappingCell = ({ t, dropdownStore, value, status }) => (
	<div className="flex--default flex--no-wrap flex--gap--1">
		{/* BaasicDropdown wraps itself in .react-select-override, which has no width of its own, so the growing
			has to happen on a wrapper or the picker collapses to the width of its placeholder. */}
		<div className="flex--grow--1">
			{dropdownStore ? (
				<BaasicDropdown dropdownStore={dropdownStore} showLabel={false} isClearable menuPlacement="auto" />
			) : (
				<label className="type--base type--color--text">{value}</label>
			)}
		</div>
		{status && (
			<i
				className={`flex--shrink--0 ${
					status.isValid ? 'type--color--success fas fa-check-circle' : 'type--color--error fas fa-times-circle'
				}`}
				title={t(status.message)}
			/>
		)}
	</div>
);

export default defaultTemplate(WarehouseCodesTemplate);
